<script setup lang="ts">
  import { ref, onMounted } from 'vue'
  import { Separator } from '@/components/ui/separator'
  import { ApplicationConfiguration } from '@/types'
  import configService from '@/services/configService'
  import DateRangeSelector from '@/components/DateRangeSelector.vue'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import {
    useExecutionHistoryStore,
    createExecutionHistoryStore
  } from '@/store/executionHistoryStore'
  import { Check, ListIcon, ListTree, RefreshCw, Search, X } from 'lucide-vue-next'
  import { useDebounceFn } from '@vueuse/core'
  import { useWindowSize } from '@/composables/useWindowSize'
  import {
    AdvancedAnalysisPhaseType,
    ExecutionPhaseType
  } from '@/shared/Executions/models/ExecutionPhaseType'
  import { computed } from 'vue'
  import { toTitleCase } from '@/utils'

  const appConfig = ref<ApplicationConfiguration | null>(null)

  // Define props for the component
  const props = defineProps<{
    isRefreshing?: boolean
    autoExpandActive?: boolean
    activeStatuses: Set<ExecutionStatusType>
    viewMode: 'tree' | 'flat'
    messages?: string[]
    searchValue?: string
    storeInstance?: ReturnType<typeof createExecutionHistoryStore>
  }>()

  const store = props.storeInstance ?? useExecutionHistoryStore()
  const { executionHistory } = store

  const { isWindowWidthAtLeast, isWindowWidthAtMost } = useWindowSize()

  const emit = defineEmits<{
    'date-ranged-changed': [void]
    'search-value-changed': [value: string]
    'status-filter-changed': [void]
    'refresh-clicked': [void]
    'toggle-auto-expand-active': [void]
    'toggle-view-mode': [void]
    'type-filter-changed': [void]
  }>()

  const searchValue = ref<string>(props.searchValue ?? '')

  // Handle search input changes
  const handleSearch = useDebounceFn((event: Event) => {
    searchValue.value = (event.target as HTMLInputElement).value.toLowerCase()
    emit('search-value-changed', searchValue.value)
  }, 300)

  // Handle date range changes
  function handleDateRangeChanged() {
    emit('date-ranged-changed')
  }

  function handleStatusFilterChanged() {
    emit('status-filter-changed')
  }

  // Writable computed maps between the Symbol array (stored in executionHistory) and the string
  // array used by Ant Design's a-select (which cannot handle Symbol values).
  const executionTypeModel = computed({
    get: (): string[] => {
      return executionHistory.executionType.map((t) =>
        t === AdvancedAnalysisPhaseType
          ? (AdvancedAnalysisPhaseType.description as string)
          : (t as string)
      )
    },
    set: (vals: string[]) => {
      executionHistory.executionType = vals.map((val) =>
        val === AdvancedAnalysisPhaseType.description
          ? AdvancedAnalysisPhaseType
          : (val as ExecutionPhaseType)
      )
      emit('type-filter-changed')
    }
  })

  function handleRefresh() {
    emit('refresh-clicked')
  }

  function handleToggleAutoExpandActive() {
    emit('toggle-auto-expand-active')
  }

  function handleToggleViewMode() {
    emit('toggle-view-mode')
  }

  const APPLICABLE_EXECUTION_STATUSES = new Set([
    ExecutionStatusType.Queued,
    ExecutionStatusType.Pending,
    ExecutionStatusType.Throttled,
    ExecutionStatusType.Running,
    ExecutionStatusType.Succeeded,
    ExecutionStatusType.Cancelled,
    ExecutionStatusType.Failed
  ])

  function getExecutionStatuses() {
    return Object.values(ExecutionStatusType)
      .filter((value) => APPLICABLE_EXECUTION_STATUSES.has(value))
      .map((value) => ({
        label: value.charAt(0).toUpperCase() + value.slice(1),
        value
      }))
  }

  function getExecutionTypes(): Array<{ label: string; value: string }> {
    const phaseTypes = Object.values(ExecutionPhaseType)
      .filter((x) => ![ExecutionPhaseType.CustomPlots, ExecutionPhaseType.CustomTables].includes(x))
      .map((value) => ({
        label:
          value === ExecutionPhaseType.NCAAnalysis ? 'NCA' : toTitleCase(value.replace(/_/g, ' ')),
        value: value as string
      }))
    return [
      ...phaseTypes,
      { label: 'Advanced Analysis', value: AdvancedAnalysisPhaseType.description! }
    ]
  }

  function clearSearch(): void {
    searchValue.value = ''
    emit('search-value-changed', searchValue.value)
  }

  function getStatusTagLabel(omitted: { value: ExecutionStatusType }[]): string {
    if (omitted.length === 1) {
      const v = omitted[0].value
      return v.charAt(0).toUpperCase() + v.slice(1)
    }
    return `${omitted.length} Statuses`
  }

  function getTypeTagLabel(omitted: { value: string }[]): string {
    if (omitted.length === 1) {
      const match = getExecutionTypes().find((t) => t.value === omitted[0].value)
      return match?.label ?? omitted[0].value
    }
    return `${omitted.length} Types`
  }

  onMounted(async () => {
    await configService.loadApplicationConfig()
    appConfig.value = configService.getApplicationConfigData()
  })
</script>

<template>
  <nav
    class="app-menu-bar flex h-16 w-full items-center justify-between gap-2 rounded-lg border px-6"
  >
    <!-- Left Side: Button Group -->
    <div class="flex items-center space-x-4">
      <a-tooltip title="Refresh History" placement="bottom">
        <button
          class="small-button flex h-10 w-10 items-center justify-center rounded border shadow"
          @click="handleRefresh"
        >
          <RefreshCw
            :class="{ 'animate-spin': isRefreshing }"
            class="h-6 w-6 select-none focus:outline-none"
          />
        </button>
      </a-tooltip>

      <DateRangeSelector
        :icon-only="isWindowWidthAtMost(1520)"
        @dateRangeChanged="handleDateRangeChanged"
      />

      <a-select
        v-model:value="executionHistory.executionStatusType"
        mode="multiple"
        size="large"
        @change="handleStatusFilterChanged"
        placeholder="All Statuses"
        allow-clear
        :max-tag-count="0"
        style="min-width: 140px; max-width: 210px"
      >
        <template #maxTagPlaceholder="omitted">
          {{ getStatusTagLabel(omitted) }}
        </template>
        <a-select-option v-for="opt in getExecutionStatuses()" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </a-select-option>
      </a-select>

      <a-select
        v-model:value="executionTypeModel"
        mode="multiple"
        size="large"
        placeholder="All Types"
        allow-clear
        :max-tag-count="0"
        style="min-width: 140px; max-width: 210px"
      >
        <template #maxTagPlaceholder="omitted">
          {{ getTypeTagLabel(omitted) }}
        </template>
        <a-select-option v-for="opt in getExecutionTypes()" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </a-select-option>
      </a-select>

      <div class="mb-[5px] flex flex-col pr-1">
        <a-tooltip
          :title="`Auto-expand executions that are or have active (${[...activeStatuses].join(', ')}) children.`"
          placement="top"
        >
          <div>
            <a-switch
              size="small"
              :checked="autoExpandActive"
              @change="handleToggleAutoExpandActive"
            >
              <template #checkedChildren>
                <div class="">
                  <Check class="h-4 w-4" />
                </div>
              </template>
              <template #unCheckedChildren>
                <div class="text-foreground">
                  <X class="h-4 w-4" />
                </div>
              </template>
            </a-switch>
            <label class="ml-1" v-if="isWindowWidthAtLeast(1400)">Auto Expand</label>
          </div>
        </a-tooltip>

        <a-tooltip title="Toggle between Tree and Flat view modes." placement="bottom">
          <div>
            <a-switch
              size="small"
              :checked="props.viewMode == 'tree'"
              @change="handleToggleViewMode"
            >
              <template #checkedChildren>
                <div class="">
                  <ListTree class="h-4 w-4" />
                </div>
              </template>
              <template #unCheckedChildren>
                <div class="text-foreground">
                  <ListIcon class="h-4 w-4" />
                </div>
              </template>
            </a-switch>
            <label class="ml-1" v-if="isWindowWidthAtLeast(1400)">View Mode</label>
          </div>
        </a-tooltip>
      </div>
    </div>

    <div class="flex items-center">
      <div class="flex h-full items-center">
        <a-input-search
          placeholder="Search local"
          :value="searchValue"
          size="large"
          style="min-width: 150px"
          @input="handleSearch"
          class="search-input-aligned"
        >
          <template #enterButton>
            <a-button @click="searchValue ? clearSearch() : undefined">
              <X v-if="searchValue?.length" class="h-4 w-4" />
              <Search v-else class="h-4 w-4" />
            </a-button>
          </template>
        </a-input-search>
      </div>
    </div>
  </nav>
  <div class="mb-2"></div>
  <div class="w-full">
    <div class="flex flex-col items-end text-xs text-muted-foreground">
      <div v-for="message in messages" class="mb-1">
        {{ message }}
      </div>
    </div>
  </div>
  <Separator class="my-6" />
</template>

<style scoped>
  .ant-btn-default {
    box-shadow: none !important;
  }

  .no-select {
    user-select: none;
  }

  .search-input-aligned .ant-input-search-button {
    display: flex !important;
    justify-content: center !important;
    align-items: center;
  }

  .ant-input-group-addon {
    background-color: var(--background) !important;
  }

  :deep(.ant-select-selector) {
    height: 40px !important;
    overflow: hidden !important;
  }

  :deep(.ant-select-selection-overflow) {
    flex-wrap: nowrap !important;
  }
</style>
